import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const CartContext = createContext();

export function CartProvider({ children }) {
  const API_BASE = import.meta.env.VITE_API_BASE_URL;

  // Load initial cart from localStorage
  const [cart, setCart] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = localStorage.getItem("relaxb_cart");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const [drawerOpen, setDrawerOpen] = useState(false);

  // Drawer controls
  const openDrawer = () => setDrawerOpen(true);
  const closeDrawer = () => setDrawerOpen(false);
  const toggleDrawer = () => setDrawerOpen((prev) => !prev);

  // Derived values
  const cartCount = cart.reduce((sum, item) => sum + (item.qty || 0), 0);
  const cartTotal = cart.reduce(
    (sum, item) => sum + (item.price || 0) * (item.qty || 0),
    0
  );

  // 🔄 Persist cart to localStorage + sync to backend when cart changes
  useEffect(() => {
    try {
      localStorage.setItem("relaxb_cart", JSON.stringify(cart));
    } catch {}

    const token = localStorage.getItem("userToken");
    if (!token) return;
    if (!cart) return;

    (async () => {
      try {
        await axios.post(
          `${API_BASE}/api/users/cart`,
          { cart },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } catch (err) {
        console.error("Sync cart error:", err);
      }
    })();
  }, [cart, API_BASE]);

  // 📥 On mount: if logged in, pull saved cart from backend once
  useEffect(() => {
    const token = localStorage.getItem("userToken");
    if (!token) return;

    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/users/cart`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const saved = res.data.cart || [];
        if (saved.length) {
          setCart(saved);
          try {
            localStorage.setItem("relaxb_cart", JSON.stringify(saved));
          } catch {}
        }
      } catch (err) {
        console.error("Load saved cart error:", err);
      }
    })();
  }, [API_BASE]);

  // ➕ Add item (supports variants)
  const addToCart = (product, options = {}) => {
    const variantName = options.variantName || null;
    const price = Number(options.price ?? product.price ?? 0);
    const qty = Number(options.qty ?? 1);

    const key = product._id + (variantName ? `__${variantName}` : "");

    setCart((prev) => {
      const existingIndex = prev.findIndex((i) => i.key === key);

      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex].qty += qty;
        return updated;
      }

      return [
        ...prev,
        {
          key,
          _id: product._id,
          name: product.name,
          images: product.images || [],
          price,
          qty,
          variantName,
          sku: options.sku || null,
        },
      ];
    });

    openDrawer();
  };

  // ❌ Remove item
  const removeFromCart = (key) => {
    setCart((prev) => prev.filter((i) => i.key !== key));
  };

  // 🔁 Update quantity
  const updateQuantity = (key, qty) => {
    if (qty <= 0) return removeFromCart(key);
    setCart((prev) =>
      prev.map((i) => (i.key === key ? { ...i, qty } : i))
    );
  };

  // 🧹 Clear all
  const clearCart = () => setCart([]);

  return (
    <CartContext.Provider
      value={{
        cart,
        cartCount,
        cartTotal,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        drawerOpen,
        openDrawer,
        closeDrawer,
        toggleDrawer,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
